import React from 'react';

import { useForm } from 'react-hook-form';
import { InputAdornment, LbInput, TitleWithDescription } from '@infoods/lifebase-ui';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import classes from './AddFromDatabaseModal.module.scss';
import { useDatabaseFoodSearch } from './hooks/useDatabaseFoodSearch';
import { LbBigModal } from '../../../../shared/components/modals';
import { useAutoSubmitForm } from '../../../../shared/hooks/useAutoSubmitForm';
import { FoodNutrientsCard } from '../../../components/FoodNutrientsCard/FoodNutrientsCard';
import { FoodType } from '../../../constants/foodType';
import { DatabaseFoodItem } from '../../../interfaces/DatabaseFoodItem';
import { getHumanizeFoodType, goToAddFoodWithTemplate } from '../../../utils';

type AddFromDatabaseModalProps = {
  /** sent as `src`, but results stay global - the backend ignores it under `my_food_diary=true` */
  foodType: FoodType;
  onClose: () => void;
};

type DatabaseFoodSearchFormValues = {
  searchTerm: string;
};

/** Mount it only while open - remounting is what clears the previous search's results */
const AddFromDatabaseModal = ({ foodType, onClose }: AddFromDatabaseModalProps) => {
  const { control, watch, handleSubmit } = useForm<DatabaseFoodSearchFormValues>();

  const { foods, isSearch, isLoading, searchFoods } = useDatabaseFoodSearch(foodType);

  useAutoSubmitForm(
    handleSubmit(({ searchTerm }) => searchFoods(searchTerm)),
    watch,
    ['searchTerm'],
  );

  const handleFoodSelect = (food: DatabaseFoodItem) => {
    goToAddFoodWithTemplate(food, foodType);
    onClose();
  };

  return (
    <LbBigModal
      open
      title={`Add ${getHumanizeFoodType(foodType)} from Database`}
      isLoading={isLoading}
      onClose={onClose}
      buttonOptions={{
        noHandler: onClose,
        noText: 'Close',
        yesVisible: false,
      }}
    >
      <LbInput
        control={control}
        name="searchTerm"
        variant="outlined"
        size="medium"
        className={classes.searchInput}
        placeholder={`Enter the ${getHumanizeFoodType(foodType, { case: 'lower' })} name`}
        dataTestId="database-food-search-input"
        autoFocus
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchRoundedIcon fontSize="large" color="tertiary" />
            </InputAdornment>
          ),
        }}
      />
      <div className={classes.foodCardList}>
        {foods.map((food) => (
          <FoodNutrientsCard
            key={food.my_net_diary_bean_id ?? food.name}
            food={food}
            actionIcon={<AddCircleRoundedIcon color="success" fontSize="large" />}
            onClick={() => handleFoodSelect(food)}
          />
        ))}
      </div>
      {!foods.length && (
        <div className={classes.emptyListContent}>
          <SearchRoundedIcon fontSize="large" color="secondary" />
          <TitleWithDescription
            titleFont="title-16-bold"
            title={
              isSearch
                ? "Your search didn't return any results"
                : 'Input a search item name'
            }
            description={
              isSearch
                ? 'Try a different search term'
                : 'Start typing to find the item you need'
            }
          />
        </div>
      )}
    </LbBigModal>
  );
};

export { AddFromDatabaseModal };
